// The signed-in account's plan, as the portal sees it: what it includes and which pages are locked.
// The catalog and the rules live in @gmx/shared (packages/shared/src/plans.ts). The API enforces the
// same rules (403 feature_locked), so this only decides what the UI shows.

import {
  PLAN_FEATURE_INFO,
  PLAN_FEATURES,
  PLAN_TIER_INFO,
  PLAN_TIERS,
  planFeatureForPath,
  planTierFor,
  type Entitlements,
  type Me,
  type PlanFeature,
  type PlanTier,
} from "@gmx/shared";
import { useMemo } from "react";
import { useAuth } from "./auth";

type Access = Pick<Entitlements, "features" | "operator"> | null | undefined;

/** May an account with these entitlements use `f`? Missing entitlements (an older API) allow everything. */
export function featureAllowed(e: Access, f: PlanFeature): boolean {
  return !e || e.operator || e.features.includes(f);
}

/** The plan feature that locks this portal path for these entitlements, or null when the page is open. */
export function lockedFeatureForPath(pathname: string, e: Access): PlanFeature | null {
  const f = planFeatureForPath(pathname);
  return f && !featureAllowed(e, f) ? f : null;
}

/**
 * Why a feature is locked: the account's tier doesn't include it (`upgrade`), or the tier does and it
 * was switched off for this account by its package or its own switches (`switched_off`).
 */
export type LockReason = "upgrade" | "switched_off";

export function lockReason(e: Pick<Entitlements, "tier"> | null | undefined, f: PlanFeature): LockReason {
  return e && PLAN_TIER_INFO[e.tier].features.includes(f) ? "switched_off" : "upgrade";
}

/** Standard tiers that include `f`, cheapest first. */
export function tiersWith(f: PlanFeature): PlanTier[] {
  return PLAN_TIERS.filter((t) => PLAN_TIER_INFO[t].features.includes(f));
}

/** "Pro", "Growth and Pro", "Starter, Growth and Pro". */
export function joinTierNames(tiers: readonly PlanTier[]): string {
  const names = tiers.map((t) => PLAN_TIER_INFO[t].name);
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** The tier below `tier`, or null for Starter. */
export function previousTier(tier: PlanTier): PlanTier | null {
  const i = PLAN_TIERS.indexOf(tier);
  return i > 0 ? (PLAN_TIERS[i - 1] ?? null) : null;
}

/** Features `tier` adds on top of the tier below it. */
export function tierAdds(tier: PlanTier): PlanFeature[] {
  return PLAN_FEATURES.filter((f) => PLAN_FEATURE_INFO[f].tier === tier);
}

export function igAccountsText(n: number): string {
  return `${n} X account${n === 1 ? "" : "s"}`;
}

/** Live campaigns per X account, as a phrase: null = unlimited, 0 = none. */
export function liveCampaignsText(n: number | null): string {
  if (n === null) return "Unlimited live campaigns";
  if (n === 0) return "No campaigns";
  return `${n} live campaign${n === 1 ? "" : "s"} per account`;
}

export interface PlanState {
  /** What /api/auth/me resolved. null when the API didn't send it (older API): everything is included. */
  entitlements: Entitlements | null;
  /** May the signed-in account use this feature? */
  has: (f: PlanFeature) => boolean;
  /** The standard tier the account is measured against. null while signed out. */
  tier: PlanTier | null;
  /** Admins and resellers: they run the product and have every feature. */
  operator: boolean;
}

export function planState(user: Pick<Me, "plan" | "role" | "entitlements"> | null | undefined): PlanState {
  const entitlements = user?.entitlements ?? null;
  return {
    entitlements,
    has: (f) => featureAllowed(entitlements, f),
    tier: entitlements?.tier ?? (user ? planTierFor(user.plan) : null),
    operator: entitlements?.operator ?? (user?.role === "admin" || user?.role === "reseller"),
  };
}

/** The signed-in account's plan. */
export function usePlan(): PlanState {
  const { user } = useAuth();
  return useMemo(() => planState(user), [user]);
}
